import { useState } from 'react'
import EstadoAsistencia from '../../../../../components/EstadoAsistencia/EstadoAsistencia'
import PageLoading from '../../../../../components/PageLoading/PageLoading'
import { useNotification } from '../../../../../context/NotificationManager/NotificationManager'
import {
  getAsistenciasFormacionSinProfesor,
  updateAsistencia
} from '../../../../../utils/api/asistenciasFormacion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dateToString, monthToString } from '../../../../../utils/date'
import { downloadCSVFile, getCSVContent } from '../../../../../utils/csv'
import { dateTimeSort, textFilter } from '../../../../../utils/table'
import TableHeader from '../../../../../components/TableHeader/TableHeader'
import { IconButton, Link, Typography } from '@mui/material'
import InformationTable from '../../../../../components/Table/InformationTable'
import MesRecienteSelector from '../../../../../components/form/MesRecienteSelector/MesRecienteSelector'
import ProfesorSelector from '../../../../../components/form/ProfesorSelector/ProfesorSelector'
import css from './Formaciones.module.css'

const Formaciones = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [mes, setMes] = useState(monthToString(new Date()))
  const [search, setSearch] = useState('')

  const { isLoading, data: formacionList = [] } = useQuery({
    queryKey: [
      'planificacion',
      'asistencia-formacion',
      'list',
      'sin-profesor',
      mes
    ],
    queryFn: () =>
      getAsistenciasFormacionSinProfesor(mes)
        .then(datos =>
          datos.map(asistencia => ({
            ...asistencia,
            fecha: dateToString(asistencia.fecha)
          }))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar las formaciones',
            content: err
          })
          return []
        })
  })

  const { isPending: isUpdatingAsistencia, mutate: modificarAsistencia } =
    useMutation({
      mutationFn: updateAsistencia,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['planificacion', 'asistencia-formacion', 'list']
        })
      },
      onError: err => {
        notification.error({
          title: 'Error al actualizar la asistencia',
          content: err
        })
      }
    })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Formación',
        'Centro',
        'Fecha',
        'Hora',
        'Asignatura',
        'Clase',
        'Profesor',
        'Observaciones'
      ],
      data: formacionList,
      transform: c => [
        c.formacion,
        c.centro,
        c.fecha,
        c.hora,
        `${c.asignatura} - ${c.nivel}`,
        c.clase,
        c.profesor,
        c.observaciones
      ]
    })
    downloadCSVFile(content, `formaciones-sin-profesor`)
  }

  const filterSearch = asistencia => {
    return textFilter({
      object: asistencia,
      fields: [
        'formacion',
        'centro',
        'fecha',
        'hora',
        'asignatura',
        'tipo',
        'nivel',
        'clase',
        'profesor',
        'observaciones'
      ],
      search: search
    })
  }

  const handleProfesorChange = (profesor, asistencia) => {
    if (isUpdatingAsistencia) return
    modificarAsistencia({
      id: asistencia.id,
      profesor: profesor.id
    })
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
            <MesRecienteSelector
              className={css.mes}
              name='mes'
              label='Mes'
              value={mes}
              forwardOptions={1}
              backwardOptions={18}
              onChange={e => setMes(e.target.value)}
            />
          </div>
        }
        title={`${formacionList.length} formaciones sin profesor`}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      {formacionList.length === 0 && (
        <div className={css.empty}>
          <Typography className={css.placeholder} variant='body1'>
            No hay formaciones sin asignar profesor
          </Typography>
        </div>
      )}
      {formacionList.length > 0 && (
        <InformationTable
          details={[
            {
              title: 'Formación',
              key: 'formacion',
              sortKey: 'formacionNombre'
            },
            { title: 'Fecha', key: 'fecha', sortFunction: dateTimeSort },
            { title: 'Asignatura', key: 'asignatura' },
            { title: 'Profesor', key: 'profesor', sortDisabled: true },
            {
              title: 'Asistencia',
              key: 'asistencia',
              align: 'center',
              sortDisabled: true
            },
            { title: 'Observaciones', key: 'observaciones', sortDisabled: true }
          ]}
          data={formacionList.filter(filterSearch).map(asistencia => ({
            key: asistencia.id,
            formacion: (
              <Link
                className={css.link}
                href={`/formaciones/${asistencia.formacionId}/ficha`}
              >
                {asistencia.formacion}
              </Link>
            ),
            formacionNombre: asistencia.formacion,
            fecha: `${asistencia.fecha} ${asistencia.hora}`,
            asignatura: asistencia.asignatura,
            profesor: (
              <ProfesorSelector
                className={css.input}
                name='profesor'
                label='Profesor'
                value={asistencia.profesorId}
                onChange={(_e, value) =>
                  handleProfesorChange(value, asistencia)
                }
              />
            ),
            asistencia: <EstadoAsistencia asistencia={asistencia} />,
            observaciones: asistencia.observaciones
          }))}
        />
      )}
    </PageLoading>
  )
}

export default Formaciones
